import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isEmpty, toString } from 'lodash';
import {
  PUBLIC_COMPS_AND_INDEXES_BY_DATE_DEFAULT_ERROR_MESSAGE,
  PUBLIC_COMPS_AND_INDEXES_BY_DATE_QUERY_KEY,
  SERVICES_FUND_INVESTMENTS_REFETCH_ON_WINDOW_FOCUS,
} from 'common/constants/services/fundInvestments';
import { CapIQPublicCompsList, CompsByDate, CompsList, CompsListByBatch } from 'pages/ValuationsAllocation/types';
import { getCompsListByBatch } from 'services/hooks/valuation';
import ValuationService from 'services/valuations';
import { getArrayValue, getObjectValue, getYearFromDate, throwError } from 'utilities';
import {
  GetPublicCompsIndexesByDateParams,
  GetPublicCompsIndexesByDateReturn,
  GetPublicCompsOrIndexesListParams,
  UseGetPublicCompsIndexesByDateParams,
} from './useGetPublicCompsIndexesByDate.types';

const DEFAULT_MEASUREMENT_DATE_ID = '0'; // Placeholder for measurement date ID

const getPublicCompsOrIndexesList = async (params: GetPublicCompsOrIndexesListParams): Promise<CompsList[]> => {
  const { compsByDate, currency, isIndex } = params;

  const valuationService = new ValuationService();

  const list = Object.entries(getObjectValue(compsByDate)).map(async ([date, tickerSymbols]) => {
    const calendarYear = toString(getYearFromDate(date));

    const getCapIQList = async (identifiers: string[]) => {
      const response = (await valuationService.getPublicCompList(
        identifiers,
        DEFAULT_MEASUREMENT_DATE_ID,
        toString(currency),
        calendarYear,
        date,
        isIndex
      )) as CapIQPublicCompsList;

      return response;
    };

    const listByBatch = (await getCompsListByBatch(tickerSymbols, getCapIQList)) as CompsListByBatch;

    const { results } = listByBatch;

    return [date, results] as CompsList;
  });

  return Promise.all(list);
};

const getPublicCompsIndexesByDate = async (params: GetPublicCompsIndexesByDateParams): Promise<CompsByDate | null> => {
  const { currency, tickersSymbolsByDateAndType } = params;

  if (isEmpty(currency) || isEmpty(tickersSymbolsByDateAndType)) {
    return throwError(PUBLIC_COMPS_AND_INDEXES_BY_DATE_DEFAULT_ERROR_MESSAGE);
  }

  const { publicCompsByDate, indexesByDate } = tickersSymbolsByDateAndType;

  try {
    const [publicCompsList, indexesList] = await Promise.all([
      getPublicCompsOrIndexesList({ compsByDate: publicCompsByDate, currency, isIndex: false }),
      getPublicCompsOrIndexesList({ compsByDate: indexesByDate, currency, isIndex: true }),
    ]);

    const publicCompsAndIndexes = [...publicCompsList, ...indexesList];

    if (isEmpty([publicCompsAndIndexes])) {
      return throwError(PUBLIC_COMPS_AND_INDEXES_BY_DATE_DEFAULT_ERROR_MESSAGE);
    }

    const publicCompsIndexesByDate = getObjectValue(
      publicCompsAndIndexes.reduce((accumulator, current) => {
        const [date, results] = current;

        accumulator[date] = [...getArrayValue(accumulator?.[date]), ...results];

        return accumulator;
      }, {} as CompsByDate)
    );

    return publicCompsIndexesByDate;
  } catch (error) {
    return throwError(PUBLIC_COMPS_AND_INDEXES_BY_DATE_DEFAULT_ERROR_MESSAGE, error);
  }
};

const useGetPublicCompsIndexesByDate = (
  params: UseGetPublicCompsIndexesByDateParams
): GetPublicCompsIndexesByDateReturn => {
  const { currency, shouldQueryAutomatically = true, tickersSymbolsByDateAndType } = params;

  const { data, error, isError, isFetching, isLoading, refetch, fetchStatus } = useQuery<
    GetPublicCompsIndexesByDateReturn['data'],
    Error
  >({
    enabled: !isEmpty(currency) && !isEmpty(tickersSymbolsByDateAndType) && shouldQueryAutomatically,
    queryFn: () => getPublicCompsIndexesByDate({ currency, tickersSymbolsByDateAndType }),
    queryKey: [PUBLIC_COMPS_AND_INDEXES_BY_DATE_QUERY_KEY, currency, tickersSymbolsByDateAndType],
    refetchOnWindowFocus: SERVICES_FUND_INVESTMENTS_REFETCH_ON_WINDOW_FOCUS,
  });

  const successData = useMemo(() => {
    if (fetchStatus === 'idle') {
      return data;
    }

    return null;
  }, [data, fetchStatus]);

  return {
    data: successData,
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  };
};

export default useGetPublicCompsIndexesByDate;
